import React from 'react';
import {
  PiUser,
  PiIdentificationCard,
  PiRadio,
  PiPhone,
  PiFlag,
  PiDatabase,
  PiBoat,
  PiBoatThin,
  PiRuler,
  PiArrowUp,
  PiCompass,
  PiNavigationArrow,
  PiGauge,
  PiMapPin,
  PiClock,
  PiGlobe,
  PiWind,
  PiDropHalf,
  PiMapTrifold,
  PiHash,
  PiWarning,
  PiCheckCircle,
  PiInfo,
  PiTarget,
  PiAirplaneTilt,
  PiSignIn,
  PiCircleThin,
  PiDiamondThin,
  PiWaveSquare,
  PiDrone,
  PiThermometer,
  PiClockCountdown,
} from 'react-icons/pi';
import { GhVirtualAton } from '../../../assets/icons/GhVirtualAton';
import { TargetCardTargetType } from './useTargetForm';

/** Canonical mapping of target card types to their icons. */
export const TARGET_TYPE_ICONS: Record<
  TargetCardTargetType,
  React.ElementType
> = {
  vessel: PiBoatThin,
  aton: PiDiamondThin,
  virtualAton: GhVirtualAton,
  methyd: PiThermometer,
  baseStation: PiCircleThin,
  radar: PiRadio,
  radarFused: PiRadio,
  das: PiWaveSquare,
  drone: PiDrone,
};

// Field display names per target type
export const FIELD_DISPLAY_NAMES: Record<string, string> = {
  // Common fields
  name: 'Name',
  mmsi: 'MMSI',
  country: 'Country',
  latitudeDegrees: 'Latitude',
  longitudeDegrees: 'Longitude',
  lastOrigin: 'Last Origin',
  targetType: 'Target Type',

  // Vessel fields
  imo: 'IMO',
  callSign: 'Call Sign',
  aisType: 'AIS Type',
  sourceType: 'Source Type',
  shipType: 'Ship Type',
  width: 'Width',
  length: 'Length',
  draught: 'Draught',
  heading: 'Heading',
  rateOfTurn: 'Rate of Turn',
  courseOverGround: 'Course Over Ground',
  speedOverGround: 'Speed Over Ground',
  destination: 'Destination',
  eta: 'ETA',
  navigationalStatus: 'Navigational Status',

  // AIS report timestamps
  lastAisReport: 'AIS Terrestrial',
  lastAisSatReport: 'AIS Satellite',
  lastAisMessage: 'Last AIS Message',
  lastAisReportSince: 'AIS Terrestrial (age)',
  lastAisSatReportSince: 'AIS Satellite (age)',
  lastAisMessageSince: 'Last AIS Message (age)',

  // ATON fields
  assignedMode: 'Assigned Mode',
  atonType: 'AtoN Type',
  offPosition: 'Off Position',
  state: 'State',
  status: 'Status',

  // Virtual ATON fields
  virtualAtonType: 'Virtual AtoN Type',

  // MetHyd fields
  stationName: 'Station Name',
  siteId: 'Site ID',
  airRelatedSettings: 'Air Related',
  waterRelatedSettings: 'Water Related',
  location: 'Location',
  message: 'Message',
  reportTimestamp: 'Report Timestamp',
  dataTimeout: 'Data Timeout',
  owner: 'Owner',

  // BaseStation fields
  vdlLoad: 'VDL Load',

  // Radar fields
  id: 'ID',
  lastRadarReport: 'Last Radar Report',

  // Radar Fused fields
  aisClass: 'AIS Class',
  course: 'COG',
  sensorId: 'Sensor ID',
  sensorType: 'Sensor Type',
  sensorTrackId: 'Sensor Track ID',
  sensorMainSource: 'Main Source',
  sensorSite: 'Sensor Site',

  // DAS fields
  trackId: 'Track ID',
  site: 'Site',
  priority: 'Priority',
  lastReportTimestamp: 'Last Report',
  accuracy: 'Accuracy',
  speed: 'Speed',

  // Drone fields
  uasId: 'UAS ID',
  macAddress: 'MAC Address',
  uaType: 'UA Type',
  altitude: 'Altitude',
  operatorLatitude: 'Operator Latitude',
  operatorLongitude: 'Operator Longitude',
  operationStatus: 'Operation Status',
};

// Get field display name with fallback
export const getFieldDisplayName = (field: string): string => {
  return FIELD_DISPLAY_NAMES[field] || field;
};

// Field icons mapping
const FIELD_ICON_MAP: Record<string, React.ElementType> = {
  // Identification
  name: PiUser,
  stationName: PiUser,
  owner: PiUser,
  imo: PiIdentificationCard,
  id: PiIdentificationCard,
  trackId: PiIdentificationCard,
  uasId: PiIdentificationCard,
  sensorTrackId: PiIdentificationCard,
  sensorId: PiIdentificationCard,
  mmsi: PiRadio,
  callSign: PiPhone,
  macAddress: PiPhone,

  // Location
  country: PiFlag,
  latitudeDegrees: PiGlobe,
  longitudeDegrees: PiGlobe,
  operatorLatitude: PiGlobe,
  operatorLongitude: PiGlobe,
  destination: PiMapPin,
  location: PiMapPin,
  site: PiMapTrifold,
  sensorSite: PiMapTrifold,

  // Type/Category
  aisType: PiDatabase,
  sourceType: PiDatabase,
  atonType: PiDatabase,
  virtualAtonType: PiDatabase,
  uaType: PiDatabase,
  aisClass: PiDatabase,
  sensorType: PiDatabase,
  shipType: PiBoat,
  targetType: PiBoat,
  navigationalStatus: PiBoat,
  operationStatus: PiBoat,

  // Dimensions
  width: PiRuler,
  length: PiRuler,
  draught: PiArrowUp,
  altitude: PiAirplaneTilt,

  // Navigation
  heading: PiCompass,
  rateOfTurn: PiCompass,
  courseOverGround: PiNavigationArrow,
  course: PiNavigationArrow,
  speedOverGround: PiGauge,
  speed: PiGauge,
  vdlLoad: PiGauge,

  // Time
  eta: PiClock,
  lastRadarReport: PiClock,
  reportTimestamp: PiClock,
  lastReportTimestamp: PiClock,
  dataTimeout: PiClock,
  lastAisReport: PiClock,
  lastAisSatReport: PiClock,
  lastAisMessage: PiClock,
  lastAisReportSince: PiClockCountdown,
  lastAisSatReportSince: PiClockCountdown,
  lastAisMessageSince: PiClockCountdown,

  // MetHyd specific
  airRelatedSettings: PiWind,
  waterRelatedSettings: PiDropHalf,
  message: PiInfo,

  // Status
  assignedMode: PiCheckCircle,
  state: PiCheckCircle,
  status: PiCheckCircle,
  sensorMainSource: PiCheckCircle,
  offPosition: PiWarning,

  // DAS specific
  priority: PiHash,
  siteId: PiHash,
  accuracy: PiTarget,
  lastOrigin: PiSignIn,
};

export const getFieldIcon = (fieldName: string) => {
  return FIELD_ICON_MAP[fieldName] ?? PiDatabase;
};

// Get the title for each target type tab
export const getTargetTypeTitle = (
  targetType: TargetCardTargetType,
): string => {
  switch (targetType) {
    case 'vessel':
      return 'Vessel';
    case 'aton':
      return 'AtoN';
    case 'virtualAton':
      return 'Virtual AtoN';
    case 'methyd':
      return 'Met/Hyd';
    case 'baseStation':
      return 'Base Station';
    case 'radar':
      return 'Radar';
    case 'radarFused':
      return 'Radar Fused';
    case 'das':
      return 'DAS';
    case 'drone':
      return 'Drone';
    default:
      return 'Unknown';
  }
};
